import { cnpj } from 'cpf-cnpj-validator';
import { z } from 'zod';

import { optionalText, paginationQuerySchema } from './common.js';

/**
 * Valida CNPJ, numérico ou alfanumérico.
 *
 * A conferência era feita à mão aqui, e só aceitava dígitos — o que **reprova
 * um CNPJ válido** emitido a partir de julho de 2026, quando os oito primeiros
 * blocos passaram a admitir letras. A conta dos dígitos verificadores muda
 * junto: no formato novo cada caractere entra pelo seu valor ASCII menos 48.
 *
 * Reescrever isso aqui seria manter uma segunda implementação da regra da
 * Receita para envelhecer sozinha. `cpf-cnpj-validator` é MIT, não tem
 * dependência nenhuma e traz os próprios tipos.
 */
export function isValidCnpj(input: string): boolean {
  return cnpj.isValid(input);
}

/** Tira só a pontuação de formatação; letra é conteúdo, não enfeite. */
export function stripCnpj(input: string): string {
  return input.replace(/[.\-/\s]/g, '').toUpperCase();
}

export const cnpjSchema = z
  .string()
  .trim()
  .transform(stripCnpj)
  .refine((value) => value === '' || isValidCnpj(value), 'CNPJ inválido')
  .transform((value) => (value === '' ? undefined : value))
  .optional();

/** Campos da empresa, sem valor padrão — o padrão vive só na criação. */
const companyFields = {
  legalName: z.string().trim().min(3, 'Informe a razão social').max(200),
  tradeName: optionalText(200),
  cnpj: cnpjSchema,
  email: z
    .string()
    .trim()
    .toLowerCase()
    .pipe(z.union([z.literal(''), z.email('E-mail inválido')]))
    .transform((value) => (value === '' ? undefined : value))
    .optional(),
  phone: optionalText(30),
  address: optionalText(300),
  notes: optionalText(2000),
  status: z.enum(['ativa', 'inativa']),
};

export const createCompanyBodySchema = z.object({
  ...companyFields,
  status: companyFields.status.default('ativa'),
});
export type CreateCompanyBody = z.infer<typeof createCompanyBodySchema>;

/**
 * `.partial()` sobre os campos **sem padrão**.
 *
 * Derivar do schema de criação carregava junto o `.default('ativa')`, e então
 * qualquer edição — trocar o telefone, por exemplo — reativava em silêncio uma
 * empresa que estava inativa.
 */
export const updateCompanyBodySchema = z.object(companyFields).partial();
export type UpdateCompanyBody = z.infer<typeof updateCompanyBodySchema>;

export const listCompaniesQuerySchema = paginationQuerySchema.extend({
  search: z.string().trim().max(120).optional(),
  status: z.enum(['ativa', 'inativa']).optional(),
  /**
   * CNPJ exato, com ou sem pontuação.
   *
   * É o que o formulário consulta antes de cadastrar: se o CNPJ já está no
   * órgão, a tela oferece a empresa que existe em vez de deixar criar a
   * segunda. A busca por texto não serve — ela casa pedaço, e meio CNPJ
   * digitado devolveria empresa que não é aquela.
   */
  cnpj: z
    .string()
    .trim()
    .max(20)
    .transform((value) => (value === '' ? undefined : stripCnpj(value)))
    .optional(),
});
export type ListCompaniesQuery = z.infer<typeof listCompaniesQuerySchema>;

export interface CompanySummary {
  readonly id: string;
  readonly legalName: string;
  readonly tradeName: string | null;
  readonly cnpj: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  /*
   * Endereço e observações vêm na listagem porque é dela que se edita.
   * Sem eles o formulário abriria com os campos vazios e, ao salvar, o `PATCH`
   * parcial manteria os valores antigos — a tela diria uma coisa e o banco
   * guardaria outra.
   */
  readonly address: string | null;
  readonly notes: string | null;
  readonly status: 'ativa' | 'inativa';
  readonly obrasCount: number;
}

/**
 * O que a consulta de CNPJ devolve, já normalizado.
 *
 * Não é o corpo da BrasilAPI: aquele tem mais de trinta campos, nomes em
 * `snake_case` e telefone colado. Aqui vem só o que preenche o formulário, com
 * os nomes que o formulário usa — assim a tela não precisa saber de onde veio.
 */
export interface CnpjLookup {
  readonly cnpj: string;
  readonly legalName: string;
  readonly tradeName: string | null;
  readonly email: string | null;
  readonly phone: string | null;
  readonly address: string | null;
  readonly city: string | null;
  readonly state: string | null;
  readonly zipCode: string | null;
  /** `"ATIVA"`, `"BAIXADA"`, `"SUSPENSA"`. Vira aviso na tela, não campo. */
  readonly situation: string | null;
}
